/**
 * Support Performance: every rule the screens share — dates, URL state,
 * request inputs and number formatting — kept out of the components so each
 * one is testable on its own.
 *
 * Dates are UTC calendar days throughout. The API takes `[from, to)`, so an
 * inclusive end day the admin picks is sent as the next day's midnight.
 */

// Performance is its own side tab, not a page under Tickets: outside
// /ai-helpdesk so the Tickets nav entry doesn't also light up here.
export const PERFORMANCE_PATH = '/dashboard/support-performance';

export const agentPath = (agentId, search = '') =>
  `${PERFORMANCE_PATH}/agent/${encodeURIComponent(agentId)}${search}`;

export const MY_PERFORMANCE_PATH = `${PERFORMANCE_PATH}/me`;

export const UNATTRIBUTED = 'UNATTRIBUTED';
export const UNCATEGORISED = 'UNCATEGORISED';
export const MAX_RANGE_DAYS = 366;
export const DEFAULT_PRESET = 'last30';
export const DETAIL_PAGE_SIZE = 25;

export const PRODUCT_OPTIONS = [
  {value: 'MOBILE', label: 'Mobile app'},
  {value: 'ACADEMY', label: 'Academy'},
];

export const LANGUAGE_OPTIONS = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];

// Ticket list tabs (`ticketStatus`) and orderings (`ticketSort`).
export const TICKET_TABS = [
  {value: 'ALL', label: 'All'},
  {value: 'RESOLVED', label: 'Closed'},
  {value: 'PENDING', label: 'Pending now'},
];

export const TICKET_SORTS = [
  {value: 'RECENTLY_RESOLVED', label: 'Recently resolved'},
  {value: 'OLDEST_OPENED', label: 'Oldest opened'},
  {value: 'NEWEST_OPENED', label: 'Newest opened'},
];

// What the rankings count: tickets opened, or tickets resolved, in the period.
export const RANKING_BASES = ['OPENED', 'RESOLVED'];

export const PRESETS = [
  {key: 'today', label: 'Today'},
  {key: 'last7', label: 'Last 7 days'},
  {key: 'last30', label: 'Last 30 days'},
  {key: 'thisMonth', label: 'This month'},
  {key: 'lastMonth', label: 'Last month'},
  {key: 'thisQuarter', label: 'This quarter'},
  {key: 'last12Months', label: 'Last 12 months'},
];

const DAY_MS = 24 * 60 * 60 * 1000;

const utcMidnight = date =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

const addDays = (date, days) => new Date(date.getTime() + days * DAY_MS);

const addMonths = (date, months) =>
  new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth() + months,
      date.getUTCDate(),
    ),
  );

/** `YYYY-MM-DD` for a UTC date. */
export const toDay = date => date.toISOString().slice(0, 10);

const fromDay = day => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return null;
  const date = new Date(`${day}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) || toDay(date) !== day ? null : date;
};

/**
 * A preset's inclusive UTC day range, `{from, to}` as `YYYY-MM-DD`. Presets
 * that include today end today; the request then runs to tomorrow's midnight.
 */
export const presetRange = (key, now = new Date()) => {
  const today = utcMidnight(now);
  const tomorrow = addDays(today, 1);
  const lastDay = today;
  switch (key) {
    case 'today':
      return {from: toDay(today), to: toDay(lastDay)};
    case 'last7':
      return {from: toDay(addDays(tomorrow, -7)), to: toDay(lastDay)};
    case 'last30':
      return {from: toDay(addDays(tomorrow, -30)), to: toDay(lastDay)};
    case 'thisMonth':
      return {
        from: toDay(
          new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)),
        ),
        to: toDay(lastDay),
      };
    case 'lastMonth': {
      const firstOfThis = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
      );
      return {
        from: toDay(addMonths(firstOfThis, -1)),
        to: toDay(addDays(firstOfThis, -1)),
      };
    }
    case 'thisQuarter': {
      const quarterMonth = Math.floor(today.getUTCMonth() / 3) * 3;
      return {
        from: toDay(
          new Date(Date.UTC(today.getUTCFullYear(), quarterMonth, 1)),
        ),
        to: toDay(lastDay),
      };
    }
    case 'last12Months':
      // Tomorrow's midnight minus twelve calendar months: never more than
      // 366 days, leap year or not.
      return {from: toDay(addMonths(tomorrow, -12)), to: toDay(lastDay)};
    default:
      return null;
  }
};

/** Inclusive days between two `YYYY-MM-DD` days, or null if either is bad. */
export const rangeDays = (from, to) => {
  const start = fromDay(from);
  const end = fromDay(to);
  if (!start || !end) return null;
  return Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
};

/** Why a range can't be sent, or null when it can. */
export const rangeError = (from, to) => {
  const days = rangeDays(from, to);
  if (days === null) return 'Pick a start and an end date.';
  if (days < 1) return 'The end date is before the start date.';
  if (days > MAX_RANGE_DAYS) return `Pick ${MAX_RANGE_DAYS} days or fewer.`;
  return null;
};

const FILTER_KEYS = ['product', 'language', 'category', 'feature'];

/**
 * The report's filters, read from the URL. Explicit dates win; a URL with no
 * usable dates falls back to the default preset. A range with no known preset
 * reads as Custom.
 */
export const readFilters = (search, now = new Date()) => {
  const params = new URLSearchParams(search);
  const presetParam = params.get('preset');
  const knownPreset = PRESETS.some(preset => preset.key === presetParam)
    ? presetParam
    : null;
  let from = params.get('from');
  let to = params.get('to');
  let preset = knownPreset;
  if (!fromDay(from) || !fromDay(to)) {
    preset = knownPreset || DEFAULT_PRESET;
    ({from, to} = presetRange(preset, now));
  }
  const agents = (params.get('agents') || '')
    .split(',')
    .map(id => id.trim())
    .filter((id, index, all) => id && all.indexOf(id) === index)
    .slice(0, 100);
  const readPage = key =>
    Math.min(Math.max(parseInt(params.get(key), 10) || 1, 1), 100000);
  const oneOf = (value, options, fallback) =>
    options.some(option => option.value === value) ? value : fallback;
  const filters = {
    preset,
    from,
    to,
    agents,
    page: readPage('page'),
    feedbackPage: readPage('feedbackPage'),
    bucket: params.get('bucket') === 'WEEK' ? 'WEEK' : 'DAY',
    basis: params.get('basis') === 'RESOLVED' ? 'RESOLVED' : 'OPENED',
    // Null means the view's own default tab (All, or Pending for an agent).
    tickets: oneOf(params.get('tickets'), TICKET_TABS, null),
    sort: oneOf(params.get('sort'), TICKET_SORTS, null),
  };
  FILTER_KEYS.forEach(key => {
    filters[key] = params.get(key) || null;
  });
  if (!PRODUCT_OPTIONS.some(option => option.value === filters.product)) {
    filters.product = null;
  }
  if (!LANGUAGE_OPTIONS.some(option => option.value === filters.language)) {
    filters.language = null;
  }
  // A feature only means something inside its category.
  if (!filters.category) filters.feature = null;
  return filters;
};

/** The URL search string for a set of filters (only what is set). */
export const writeFilters = filters => {
  const params = new URLSearchParams();
  if (filters.preset) params.set('preset', filters.preset);
  params.set('from', filters.from);
  params.set('to', filters.to);
  FILTER_KEYS.forEach(key => {
    if (filters[key]) params.set(key, filters[key]);
  });
  if (filters.agents?.length) params.set('agents', filters.agents.join(','));
  if (filters.page > 1) params.set('page', String(filters.page));
  if (filters.feedbackPage > 1) {
    params.set('feedbackPage', String(filters.feedbackPage));
  }
  if (filters.bucket === 'WEEK') params.set('bucket', 'WEEK');
  if (filters.basis === 'RESOLVED') params.set('basis', 'RESOLVED');
  if (filters.tickets) params.set('tickets', filters.tickets);
  if (filters.sort) params.set('sort', filters.sort);
  return `?${params.toString()}`;
};

/**
 * Filters after a change, with the knock-on resets the spec requires:
 * product or language clears category and feature, category clears feature,
 * and any filter change returns the ticket list to page 1.
 */
export const changeFilters = (current, patch) => {
  const next = {...current, ...patch};
  if ('product' in patch || 'language' in patch) {
    next.category = null;
    next.feature = null;
  }
  if ('category' in patch) next.feature = null;
  if ('from' in patch || 'to' in patch) {
    // Explicit dates are Custom unless the change names its preset.
    next.preset = patch.preset || null;
  }
  // Each ticket list pages on its own; any other change starts both over.
  if (!('page' in patch)) next.page = 1;
  if (!('feedbackPage' in patch)) next.feedbackPage = 1;
  return next;
};

const isoStart = day => `${day}T00:00:00.000Z`;

const isoEndExclusive = day =>
  toDay(addDays(fromDay(day), 1)) + 'T00:00:00.000Z';

/** Filters shared by the overview and the detail request. */
const sharedInput = filters => ({
  from: isoStart(filters.from),
  to: isoEndExclusive(filters.to),
  // Absent means "all"; ALL is never sent.
  ...(filters.product && {product: filters.product}),
  ...(filters.language && {language: filters.language}),
  ...(filters.category && {categoryId: filters.category}),
  ...(filters.feature && {featureId: filters.feature}),
});

export const overviewInput = filters => ({
  ...sharedInput(filters),
  ...(filters.agents?.length && {agentIds: filters.agents}),
  rankingBasis: filters.basis || 'OPENED',
});

/**
 * The visible ticket list's filters. Pending lists default to oldest first,
 * so the longest wait is on top; others to the server's default order.
 */
const ticketInput = (filters, defaultTab) => {
  const ticketStatus = filters.tickets || defaultTab;
  const ticketSort =
    filters.sort || (ticketStatus === 'PENDING' ? 'OLDEST_OPENED' : null);
  return {ticketStatus, ...(ticketSort && {ticketSort})};
};

/**
 * Agent detail and personal view: rankings are what the agent *handled*
 * (resolved), per the spec's "top issues handled".
 */
const personInput = (filters, defaultTab) => ({
  ...sharedInput(filters),
  rankingBasis: 'RESOLVED',
  bucket: filters.bucket,
  page: filters.page,
  limit: DETAIL_PAGE_SIZE,
  ...ticketInput(filters, defaultTab),
});

// The ticket tab each view opens on: an agent's own view leads with what is
// waiting on them.
export const DETAIL_DEFAULT_TAB = 'ALL';
export const MY_DEFAULT_TAB = 'PENDING';

export const detailInput = (filters, agentId) => ({
  ...personInput(filters, DETAIL_DEFAULT_TAB),
  agentId,
});

/** The CSV of every ticket the visible list matches; no page or limit. */
export const exportInput = (filters, agentId) => {
  const input = detailInput(filters, agentId);
  delete input.page;
  delete input.limit;
  return input;
};

/** The signed-in agent's own view. Never carries an agent id. */
export const myInput = filters => personInput(filters, MY_DEFAULT_TAB);

/** The personal feedback list: rated closures, newest resolution first. */
export const myFeedbackInput = filters => ({
  ...sharedInput(filters),
  rankingBasis: 'RESOLVED',
  ticketStatus: 'RESOLVED',
  ticketSort: 'RECENTLY_RESOLVED',
  ratedOnly: true,
  page: filters.feedbackPage,
  limit: DETAIL_PAGE_SIZE,
});

/**
 * Whether the Unattributed row means anything for the chosen agents. With
 * specific people picked and Unattributed not among them, its number would
 * read as the whole team's — so it is left out.
 */
export const showUnattributed = agents =>
  !agents?.length || agents.includes(UNATTRIBUTED);

// Formatting ----------------------------------------------------------------

/** Clock time from seconds: 45s, 12m, 3h 20m, 2d 4h. Null → "Not available". */
export const formatDuration = seconds => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return 'Not available';
  }
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  // Round once at the smallest shown unit, then split, so 18h 59m 40s reads
  // "19h" rather than "18h 60m".
  const minutes = Math.round(total / 60);
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 24 * 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const totalHours = Math.round(total / 3600);
  const days = Math.floor(totalHours / 24);
  const restHours = totalHours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
};

/** A target in seconds as words: "24 hours", "90 minutes", "1 day 6 hours". */
export const formatTarget = seconds => {
  if (!seconds) return 'Not set';
  const plural = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;
  if (seconds % 3600 === 0) return plural(seconds / 3600, 'hour');
  if (seconds % 60 === 0) return plural(seconds / 60, 'minute');
  return formatDuration(seconds);
};

/**
 * The backend's first-response verdict as a label and tone. NO_DATA is
 * neutral: a missing average is never a pass.
 */
export const FIRST_RESPONSE_STATUS = {
  MEETING_TARGET: {label: 'Within target', tone: 'good'},
  EXCEEDING_TARGET: {label: 'Slower than target', tone: 'bad'},
  NO_DATA: {label: 'Not enough data', tone: 'neutral'},
};

export const firstResponseStatus = firstResponse =>
  FIRST_RESPONSE_STATUS[firstResponse?.status] || FIRST_RESPONSE_STATUS.NO_DATA;

/** How long a pending ticket has been open, from its `openSeconds`. */
export const formatAge = seconds =>
  seconds === null || seconds === undefined
    ? '—'
    : `${formatDuration(seconds)} old`;

/** The browser download for a CSV the server already built and escaped. */
export const downloadCsv = ({filename, csv}) => {
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'support-tickets.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const formatCount = value =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat('en-US').format(value);

/** "4.50 / 5", or "No ratings" when nothing was rated. */
export const formatAverageRating = average =>
  average === null || average === undefined
    ? 'No ratings'
    : `${Number(average).toFixed(2)} / 5`;

/** Coverage is already a percentage: 20 means 20%. */
export const formatCoverage = coverage =>
  coverage === null || coverage === undefined
    ? '—'
    : `${Number(coverage).toFixed(Number.isInteger(coverage) ? 0 : 1)}%`;

/**
 * The full rating line the spec asks for wherever an average rating appears:
 * "4.50 / 5 · 2 / 10 rated · 20% coverage". `resolved` is the denominator.
 */
export const ratingSummary = metrics => {
  if (!metrics) return 'No ratings';
  const {averageRating, ratingCount, ratingCoverage, resolved} = metrics;
  const counts = `${formatCount(ratingCount || 0)} / ${formatCount(
    resolved || 0,
  )} rated`;
  if (averageRating === null || averageRating === undefined) {
    return `No ratings · ${counts}`;
  }
  return `${formatAverageRating(averageRating)} · ${counts} · ${formatCoverage(
    ratingCoverage,
  )} coverage`;
};

/** Previous-period change: "+12%", "−5%", "New", or "—". */
export const formatChange = ({changePercentage, count, previousCount}) => {
  if (changePercentage === null || changePercentage === undefined) {
    return count > 0 && !previousCount ? 'New' : '—';
  }
  const rounded = Math.round(changePercentage);
  if (rounded === 0) return '0%';
  return rounded > 0 ? `+${rounded}%` : `−${Math.abs(rounded)}%`;
};

export const languageLabel = code =>
  LANGUAGE_OPTIONS.find(option => option.value === code)?.label || code || '—';

export const productLabel = code =>
  PRODUCT_OPTIONS.find(option => option.value === code)?.label || code || '—';

/** A person's name, or a readable fallback when the profile is missing. */
export const agentName = (agent, agentId) => {
  if (agentId === UNATTRIBUTED) return 'Unattributed';
  const name = [agent?.firstName, agent?.lastName].filter(Boolean).join(' ');
  if (name) return name;
  const id = agent?.id || agentId;
  return id ? `Agent ${String(id).slice(-6)}` : 'Unknown agent';
};

export const customerName = customer => {
  if (!customer) return 'Anonymous';
  const name = [customer.firstName, customer.lastName]
    .filter(Boolean)
    .join(' ');
  return name || `Customer ${String(customer.id || '').slice(-6)}`;
};

/** "2 tickets", for counts in sentences. */
export const ticketCount = count =>
  `${formatCount(count || 0)} ticket${count === 1 ? '' : 's'}`;

/**
 * Sort comparator for the agent table. Nulls (no ratings, no measured time)
 * always sink to the bottom, whichever way the column is sorted.
 */
export const compareNullable = (a, b, direction = 'ascend') => {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return direction === 'ascend' ? 1 : -1;
  if (bNull) return direction === 'ascend' ? -1 : 1;
  if (typeof a === 'string') return a.localeCompare(b);
  return a - b;
};

/** "1 Sep 2026 – 30 Sep 2026 (UTC)" for an inclusive day range. */
export const describeRange = (from, to) => {
  const format = day =>
    fromDay(day)?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }) || day;
  return from === to
    ? `${format(from)} (UTC)`
    : `${format(from)} – ${format(to)} (UTC)`;
};

/** A UTC timestamp for "Pending now · as of …". */
export const formatAsOf = iso => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })} UTC`;
};
